import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, catchError } from 'rxjs';

/** Backend code for "your token's organization is not the one this host belongs to". */
const ORG_MISMATCH_CODE = 'ORG_MISMATCH';

/** Where a mismatched session is parked. Lives under the guard-free `empty` layout. */
export const ORG_MISMATCH_URL = '/errors/org-mismatch';

/**
 * True for the backend's cross-tenant rejection (be-app `TenantResolverMiddleware`,
 * PFL-1234): the request's Origin resolved to one organization while the caller's
 * token asserts another.
 *
 * Reads the code off the Nera envelope (`{ error: { code } }`). Status alone is not
 * enough — an ordinary permission failure is also a 403 and must keep its normal
 * toast-and-stay behaviour.
 */
export function isOrgMismatchError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 403) {
        return false;
    }
    const body = error.error as { error?: { code?: string } } | null;
    return body?.error?.code === ORG_MISMATCH_CODE;
}

/**
 * Latches the first mismatch so a burst of parallel 403s produces one navigation
 * instead of one per in-flight request. Root-provided (singleton) and resettable so
 * tests are not order-dependent.
 */
@Injectable({ providedIn: 'root' })
export class OrgMismatchState {
    private _redirected = false;

    /** Returns true only for the first caller; subsequent calls are no-ops. */
    claim(): boolean {
        if (this._redirected) {
            return false;
        }
        this._redirected = true;
        return true;
    }

    reset(): void {
        this._redirected = false;
    }
}

/**
 * Sign-out and session-termination endpoints.
 *
 * These must NEVER be swallowed into `EMPTY` (PFL-1908). The swallow is justified below by
 * "the app is navigating away from whatever requested it" — true for ordinary requests, but
 * false for sign-out FROM the mismatch page: the user is already on ORG_MISMATCH_URL, so
 * `state.claim()` does not re-navigate, and the caller is not navigating away — it is waiting
 * to be told the sign-out finished so it can move to /sign-in.
 *
 * `EMPTY` completes without emitting, so neither `next` nor `error` runs. That deadlocked the
 * recovery button AND skipped `_finishLocalSignOut()`, because `AuthService.signOut()` clears
 * local state from a `tap()` that only fires on an emission. The user was left signed in, on a
 * host their session cannot use, with the one recovery action inert.
 *
 * Letting the error propagate is correct here: `signOut()` already has
 * `catchError(() => of(null))`, so a failed revoke still clears local state and redirects.
 */
const SESSION_TERMINATION_PATHS = [
    '/api/v1/auth/session/logout',
    '/me/sessions/others',
] as const;

function isSessionTerminationRequest(url: string): boolean {
    return SESSION_TERMINATION_PATHS.some((path) => url.includes(path));
}

/**
 * Turns a cross-tenant rejection into a single explanatory page.
 *
 * On a mismatched host EVERY request 403s, so the default treatment — a toast per
 * failed call — produces a storm of identical "Access denied" messages that say
 * nothing about the actual problem, and leaves the user staring at a half-rendered
 * shell. This intercepts that specific code, navigates once, and swallows the error
 * so nothing downstream toasts it.
 *
 * EXCEPTION — session termination is never swallowed. See `isSessionTerminationRequest`.
 *
 * MUST be registered AFTER `neraApiErrorInterceptor` in the interceptor array.
 * Angular runs the array outside-in on the way down and inside-out on the way back,
 * so the LAST entry sees the error FIRST — which is the only position from which the
 * shared interceptor's toast can be pre-empted. Returning `EMPTY` completes the
 * stream without a value, so `neraApiErrorInterceptor`'s `catchError` never runs.
 * Callers get a completed-without-emitting observable, which is harmless because the
 * app is navigating away from whatever requested it.
 */
export const orgMismatchInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const state = inject(OrgMismatchState);

    return next(req).pipe(
        catchError((error: unknown) => {
            if (!isOrgMismatchError(error)) {
                throw error;
            }

            // Never swallow a session-termination response (PFL-1908): the caller is not
            // navigating away, it is waiting to learn the session is gone.
            if (isSessionTerminationRequest(req.url)) {
                throw error;
            }

            if (state.claim()) {
                void router.navigateByUrl(ORG_MISMATCH_URL).catch(() => undefined);
            }

            return EMPTY;
        })
    );
};
